import { useEffect, useMemo, useState } from 'react'
import {
	Box, Checkbox, CircularProgress, FormControl, InputLabel,
	ListItemText, ListSubheader, MenuItem, Select, Typography,
} from '@mui/material'

import useInternalFetch from '../api/apiInternal'
import config from '../common/Config'

/**
 * Dual-select for sector + industry.
 *
 * value   : [{ market: 'BR'|'US', sector: string, industry: string|null }]
 * onChange: (newValue) => void
 * size    : 'small' | 'medium'  (default 'small')
 *
 * Internally tracks unique sector keys and unique industries to avoid
 * the Cartesian-product explosion that arises when combining both into
 * a flat array and then re-deriving state from it on every render.
 */
const SectorIndustrySelect = ({ value = [], onChange, size = 'small' }) => {
	const internalFetch = useInternalFetch()

	const [allSectors, setAllSectors]       = useState([])  // [{ market, sector }]
	const [allIndustries, setAllIndustries] = useState([])  // string[]
	const [loadingSectors, setLoadingSectors]       = useState(true)
	const [loadingIndustries, setLoadingIndustries] = useState(false)

	// ── Derive unique sector keys and industries from props ─────────────────
	// Use memo to keep stable references and avoid effect infinite loops
	const selectedSectorKeys = useMemo(
		() => [...new Set(value.map(v => `${v.market}|${v.sector}`))],
		[value]
	)
	const selectedIndustries = useMemo(
		() => [...new Set(value.map(v => v.industry).filter(Boolean))],
		[value]
	)

	// ── Load all sectors once ────────────────────────────────────────────────
	useEffect(() => {
		Promise.all([
			internalFetch(config.apiUrl + '/api/companies/sectors/BR'),
			internalFetch(config.apiUrl + '/api/companies/sectors/US'),
		]).then(([br, us]) => {
			const rows = [
				...(Array.isArray(br) ? br : []).map(r => ({ market: 'BR', sector: r.sector })),
				...(Array.isArray(us) ? us : []).map(r => ({ market: 'US', sector: r.sector })),
			].filter(r => r.sector)
			setAllSectors(rows)
			setLoadingSectors(false)
		})
	}, [])

	// ── Reload industries when sector selection changes ──────────────────────
	const sectorKeysStr = selectedSectorKeys.join(',')
	useEffect(() => {
		if (!sectorKeysStr) { setAllIndustries([]); return }
		setLoadingIndustries(true)
		const sectorNames = selectedSectorKeys.map(k => k.split('|')[1]).join(',')
		internalFetch(config.apiUrl + '/api/companies/industries?sectors=' + encodeURIComponent(sectorNames))
			.then(res => {
				const rows = (Array.isArray(res) ? res : []).map(r => r.industry).filter(Boolean)
				setAllIndustries(rows)
				setLoadingIndustries(false)
			})
	}, [sectorKeysStr])

	// ── Emit the flat [{ market, sector, industry }] array ───────────────────
	const emitValue = (sectorKeys, industries) => {
		if (industries.length === 0) {
			onChange(sectorKeys.map(key => {
				const [market, sector] = key.split('|')
				return { market, sector, industry: null }
			}))
		} else {
			onChange(sectorKeys.flatMap(key => {
				const [market, sector] = key.split('|')
				return industries.map(industry => ({ market, sector, industry }))
			}))
		}
	}

	const handleSectorChange = (e) => {
		const keys = e.target.value
		// Retain only industries that are still valid for the new sector set
		const validIndustries = selectedIndustries.filter(i => allIndustries.includes(i))
		emitValue(keys, validIndustries)
	}

	const handleIndustryChange = (e) => {
		emitValue(selectedSectorKeys, e.target.value)
	}

	// ── Render ───────────────────────────────────────────────────────────────
	const brSectors = allSectors.filter(s => s.market === 'BR')
	const usSectors = allSectors.filter(s => s.market === 'US')

	return (
		<Box display="flex" flexDirection="column" gap={1.5}>
			{/* Sector select */}
			<FormControl fullWidth size={size}>
				<InputLabel shrink>Setor</InputLabel>
				{loadingSectors ? (
					<Box display="flex" alignItems="center" gap={1} px={1.5} py={1}>
						<CircularProgress size={16} />
						<Typography variant="caption" color="text.secondary">Carregando…</Typography>
					</Box>
				) : (
					<Select
						label="Setor"
						multiple
						displayEmpty
						value={selectedSectorKeys}
						onChange={handleSectorChange}
						renderValue={sel =>
							sel.length === 0
								? <Typography variant="body2" color="text.secondary">Todos os setores</Typography>
								: sel.map(k => k.split('|')[1]).join(', ')
						}
					>
						{brSectors.length > 0 && <ListSubheader>Brasil (BR)</ListSubheader>}
						{brSectors.map(s => {
							const key = `${s.market}|${s.sector}`
							return (
								<MenuItem key={key} value={key} dense>
									<Checkbox size="small" checked={selectedSectorKeys.includes(key)} />
									<ListItemText primary={s.sector} />
								</MenuItem>
							)
						})}
						{usSectors.length > 0 && <ListSubheader>Estados Unidos (US)</ListSubheader>}
						{usSectors.map(s => {
							const key = `${s.market}|${s.sector}`
							return (
								<MenuItem key={key} value={key} dense>
									<Checkbox size="small" checked={selectedSectorKeys.includes(key)} />
									<ListItemText primary={s.sector} />
								</MenuItem>
							)
						})}
					</Select>
				)}
			</FormControl>

			{/* Industry select — only shown when sectors are selected */}
			{selectedSectorKeys.length > 0 && (
				<FormControl fullWidth size={size}>
					<InputLabel shrink>Indústria</InputLabel>
					{loadingIndustries ? (
						<Box display="flex" alignItems="center" gap={1} px={1.5} py={1}>
							<CircularProgress size={16} />
							<Typography variant="caption" color="text.secondary">Carregando…</Typography>
						</Box>
					) : (
						<Select
							label="Indústria"
							multiple
							displayEmpty
							value={selectedIndustries}
							onChange={handleIndustryChange}
							renderValue={sel =>
								sel.length === 0
									? <Typography variant="body2" color="text.secondary">Todas as indústrias</Typography>
									: sel.join(', ')
							}
						>
							{allIndustries.map(ind => (
								<MenuItem key={ind} value={ind} dense>
									<Checkbox size="small" checked={selectedIndustries.includes(ind)} />
									<ListItemText primary={ind} />
								</MenuItem>
							))}
						</Select>
					)}
				</FormControl>
			)}
		</Box>
	)
}

export default SectorIndustrySelect
